import { createContext, useContext, useState, useEffect } from "react";
import { useToken } from "../services/contextAPi/TokenContext"; // Import useToken
import { cancelAllRequests } from "../config/api.config"; // Import the cancellation function

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);

  const { isValidToken, isCheckingToken } = useToken(); // Use the useToken hook

  useEffect(() => {
    // Check if user is logged in when component mounts
    const checkAuth = () => {
      try {
        const storedToken = localStorage.getItem("authToken");
        const storedUser = localStorage.getItem("user");

        // Only proceed if token check is complete and token is valid
        if (!isCheckingToken) {
          if (isValidToken && storedToken && storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
              setUserRole(parsedUser.role); // Access role from the parsed user object
              setAuthToken(storedToken);
              setIsAuthenticated(true);
            } catch (error) {
              console.error("Error parsing stored user data:", error);
              // Clean up invalid storage data
              try {
                localStorage.removeItem("authToken");
                localStorage.removeItem("user");
              } catch (e) {
                console.warn("Unable to clear localStorage:", e);
              }
              setIsAuthenticated(false); // Ensure isAuthenticated is false on error
            }
          } else {
            // If token is not valid or not present, ensure auth state is cleared
            try {
              localStorage.removeItem("authToken");
              localStorage.removeItem("user");
            } catch (e) {
              console.warn("Unable to clear localStorage:", e);
            }
            setUser(null);
            setUserRole(null);
            setAuthToken(null);
            setIsAuthenticated(false);
          }
          setLoading(false);
        }
      } catch (error) {
        // Handle localStorage access denied error
        console.error("localStorage access denied:", error);
        setUser(null);
        setUserRole(null);
        setAuthToken(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    checkAuth();
  }, [isValidToken, isCheckingToken]); // Depend on isValidToken and isCheckingToken

  const login = (userData, token) => {
    if (!userData || !token) {
      console.error("Invalid login data provided:", { userData, token });
      return false;
    }

    try {
      // Try to use localStorage, but gracefully handle if it's blocked
      try {
        localStorage.setItem("authToken", token);
        localStorage.setItem("user", JSON.stringify(userData));
      } catch (storageError) {
        console.warn("localStorage not available, using session-only auth:", storageError);
        // Continue with in-memory auth even if localStorage fails
      }
      
      setUser(userData);
      setUserRole(userData.role); // Set userRole when logging in
      setAuthToken(token);
      setIsAuthenticated(true);

      return true;
    } catch (error) {
      console.error("Error during login:", error);
      return false;
    }
  };

  const logout = () => {
    cancelAllRequests(); // Cancel all pending API requests
    
    // Try to clear localStorage, but don't fail if it's blocked
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
    } catch (error) {
      console.warn("Unable to clear localStorage:", error);
    }
    
    setUser(null);
    setUserRole(null); // Reset userRole when logging out
    setAuthToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        userRole,
        authToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
